import { useEffect, useMemo } from 'react'
import {
  AtendimentoType,
  formatDateToPtBr,
  initialQueryState,
  OptionsHelper,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { useAuth } from '../../../../../auth'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  DateInput,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

export const MonitoramentoListSearchComponent = () => {
  const { currentUser } = useAuth()
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const optionsTiposAtendimento = useMemo(
    () => OptionsHelper.getOptionsTiposAtendimentoUsuario(currentUser),
    [currentUser]
  )

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.empresa ? `paciente.empresa:${debouncedFilters.empresa.value}` : '',
        debouncedFilters.tipoAtendimento ? `tipo:${debouncedFilters.tipoAtendimento.value}` : '',
        debouncedFilters.estado ? `estado:${debouncedFilters.estado.value}` : '',
        debouncedFilters.especialidade
          ? `especialidade:${debouncedFilters.especialidade.value}`
          : '',
        debouncedFilters.protocolo ? `protocolo~*${debouncedFilters.protocolo.trim()}*` : '',
        debouncedFilters.paciente
          ? `paciente.pessoa.nome~*${debouncedFilters.paciente.trim()}*`
          : '',
        debouncedFilters.solicitacao ? `solicitacao>${debouncedFilters.solicitacao}T00:00:00` : '',
        debouncedFilters.inicio ? `inicio>${debouncedFilters.inicio}T00:00:00` : '',
        debouncedFilters.unidade ? `unidade.id:${debouncedFilters.unidade.value}` : '',
        debouncedFilters.profissional
          ? `responsavel.pessoa.id:${debouncedFilters.profissional.value}`
          : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Unidade depende da instituição: sem instituição, não há unidade.
  useEffect(() => {
    if (!filters.empresa && filters.unidade) setFilter('unidade', undefined)
  }, [filters.empresa, filters.unidade, setFilter])

  const empresaParams = useMemo(
    () => ({ empresaId: Number(filters.empresa?.value) }),
    [filters.empresa?.value]
  )

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo. Datas puras via split
  // (formatDateToPtBr), sem `new Date`, para não voltar um dia no fuso do Brasil.
  const activeFilters: ActiveFilter[] = [
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('cols.paciente'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('cols.protocolo'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.estado && {
      key: 'estado',
      label: t('cols.estado'),
      value: filters.estado.label,
      onRemove: () => setFilter('estado', undefined),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.unidade && {
      key: 'unidade',
      label: 'Unidade/Setor',
      value: filters.unidade.label,
      onRemove: () => setFilter('unidade', undefined),
    },
    filters.tipoAtendimento && {
      key: 'tipoAtendimento',
      label: t('tipoAtendimento.label'),
      value: filters.tipoAtendimento.label,
      onRemove: () => setFilter('tipoAtendimento', undefined),
    },
    filters.especialidade && {
      key: 'especialidade',
      label: t('especialidade.label'),
      value: filters.especialidade.label,
      onRemove: () => setFilter('especialidade', undefined),
    },
    filters.profissional && {
      key: 'profissional',
      label: 'Profissional',
      value: filters.profissional.label,
      onRemove: () => setFilter('profissional', undefined),
    },
    filters.solicitacao && {
      key: 'solicitacao',
      label: 'Agendados a partir de',
      value: formatDateToPtBr(filters.solicitacao),
      onRemove: () => setFilter('solicitacao', ''),
    },
    filters.inicio && {
      key: 'inicio',
      label: 'Iniciados a partir de',
      value: formatDateToPtBr(filters.inicio),
      onRemove: () => setFilter('inicio', ''),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  const moreFiltersActiveCount = [
    filters.empresa,
    filters.unidade,
    filters.tipoAtendimento,
    filters.especialidade,
    filters.profissional,
    filters.solicitacao,
    filters.inicio,
  ].filter(Boolean).length

  return (
    <TableFilters
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label={t('empresa.label')}>
            <CustomSelect
              entity='empresa'
              onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsEmpresas}
              value={filters.empresa}
            />
          </TableFilter>
          <TableFilter label='Unidade/Setor'>
            <CustomSelect
              placeholder={
                filters.empresa
                  ? 'Selecione uma unidade'
                  : `Selecione a ${t('empresa.label.lowercase')}`
              }
              onChange={(unidade?: IOption<number>) => setFilter('unidade', unidade ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsUnidades}
              fetchOptionsParams={empresaParams}
              value={filters.unidade}
              isDisabled={!filters.empresa}
            />
          </TableFilter>
          <TableFilter label={t('tipoAtendimento.label')}>
            <CustomSelect
              placeholder='Selecione um tipo'
              options={optionsTiposAtendimento}
              value={filters.tipoAtendimento}
              onChange={(tipo?: IOption<AtendimentoType>) =>
                setFilter('tipoAtendimento', tipo ?? undefined)
              }
            />
          </TableFilter>
          <TableFilter label={t('especialidade.label')}>
            <CustomSelect
              placeholder='Selecione uma especialidade'
              fetchOptions={OptionsHelper.getOptionsEspecialidades}
              value={filters.especialidade}
              onChange={(especialidade?: IOption<number>) =>
                setFilter('especialidade', especialidade ?? undefined)
              }
            />
          </TableFilter>
          <TableFilter label='Profissional'>
            <CustomSelect
              placeholder='Selecione um profissional'
              fetchOptions={OptionsHelper.getOptionsProfissionalSaude}
              value={filters.profissional}
              onChange={(profissional?: IOption<number>) =>
                setFilter('profissional', profissional ?? undefined)
              }
            />
          </TableFilter>
          <TableFilter label='Agendados a partir de'>
            <DateInput
              value={filters.solicitacao}
              onChange={(e) => setFilter('solicitacao', e.target.value)}
            />
          </TableFilter>
          <TableFilter label='Iniciados a partir de'>
            <DateInput
              value={filters.inicio}
              onChange={(e) => setFilter('inicio', e.target.value)}
            />
          </TableFilter>
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'atendimento encontrado' : 'atendimentos encontrados'}`
          }
        />
      }
    >
      <TableFilter label={t('cols.paciente')} wide>
        <Input
          placeholder='Digite o nome do paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.protocolo')}>
        <Input
          placeholder='Digite o protocolo'
          value={filters.protocolo}
          onChange={(e) => setFilter('protocolo', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.estado')}>
        <CustomSelect
          placeholder='Selecione um estado'
          onChange={(estado?: IOption<number>) => setFilter('estado', estado ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEstadosAtendimento}
          value={filters.estado}
        />
      </TableFilter>
    </TableFilters>
  )
}
